import { memo, useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { api } from '../lib/api';
import type { AnnotationCategory } from '../lib/types';

/** One coloured rectangle to paint on top of the canvas. */
export type Highlight = {
  /** All in CSS pixels at scale=1 (caller multiplies by current scale). */
  x: number;
  y: number;
  w: number;
  h: number;
  category: AnnotationCategory;
  annotationId: string;
};

/**
 * Search-match overlay rectangle (Sprint 4-4 / ADR-063).
 * Coordinates are in OCR image pixels — the component rescales to
 * the current pdf.js viewport via ``ocrImageWidth``.
 */
export type SearchBbox = {
  x: number;
  y: number;
  w: number;
  h: number;
};

const HL_CLASS: Record<AnnotationCategory, string> = {
  favorable: 'hl-fav',
  defavorable: 'hl-def',
  important: 'hl-imp',
  a_verifier: 'hl-ver',
};

type Props = {
  doc: PDFDocumentProxy;
  pageNumber: number;
  /** Display scale. Highlight coords get multiplied by this. */
  scale: number;
  /** Highlights to render — coordinates are at scale=1. */
  highlights?: Highlight[];
  /** Search-match bboxes — coordinates are in OCR image pixels. */
  searchHighlights?: SearchBbox[];
  /** OCR image width (px) — denominator for the ocr → css rescale. */
  ocrImageWidth?: number;
  /**
   * URL base pour le fallback OCR text layer (sprint big-PDF, 2026-05-22).
   * Ex: ``/pdfs/<uuid>`` ou ``/pieces-client/<uuid>``. Si fourni ET que
   * le pdf.js TextLayer ne génère aucun span (= PDF scanné sans texte
   * natif), on fetch ``${ocrSourceBaseUrl}/ocr/pages/{n}`` et on construit
   * un text layer custom à partir des bbox words OCR — la sélection
   * souris fonctionne alors normalement.
   */
  ocrSourceBaseUrl?: string;
  onLayoutReady?: (pageNumber: number, container: HTMLDivElement) => void;
};

/**
 * Render a single PDF page on a <canvas> + a transparent text layer for
 * selection + a highlight layer for persistent annotation overlays.
 *
 * Stack order (bottom up):
 *   1. canvas        — rendered page bitmap
 *   2. highlightLayer — annotation overlays (pointer-events: none)
 *   3. textLayer     — invisible glyph spans (selection target)
 */
function PdfPageInner({
  doc,
  pageNumber,
  scale,
  highlights = [],
  searchHighlights = [],
  ocrImageWidth,
  ocrSourceBaseUrl,
  onLayoutReady,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textLayerRef = useRef<HTMLDivElement>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function render() {
      try {
        // Si un render précédent tourne encore sur ce canvas, l'annuler
        // proprement avant d'en lancer un nouveau (sinon pdf.js lève
        // « Cannot use the same canvas during multiple render() operations »).
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          try {
            await renderTaskRef.current.promise;
          } catch {
            // RenderingCancelledException — attendu, on ignore.
          }
          renderTaskRef.current = null;
        }

        const page = await doc.getPage(pageNumber);
        if (cancelled) return;
        const viewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        const textLayer = textLayerRef.current;
        const container = containerRef.current;
        if (!canvas || !textLayer || !container) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas 2d unavailable');

        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        setViewportWidth(viewport.width / scale);
        textLayer.style.width = `${viewport.width}px`;
        textLayer.style.height = `${viewport.height}px`;
        container.style.width = `${viewport.width}px`;
        container.style.height = `${viewport.height}px`;
        // Les deux CSS vars sont nécessaires depuis pdfjs-dist 4.6 :
        //  - --scale-factor : taille de chaque glyphe (transform: scale(...))
        //  - --total-scale-factor : utilisé par .endOfContent et certaines
        //    métriques internes du textLayer. Sans cette deuxième variable
        //    les spans sont positionnés à scale=1 alors que le canvas est
        //    à scale=X → la sélection clique « dans le vide » et le browser
        //    démarre la sélection depuis le début de la page.
        container.style.setProperty('--scale-factor', String(scale));
        container.style.setProperty('--total-scale-factor', String(scale));

        // Reset du contexte 2D : pdf.js fait setTransform() en interne mais
        // certaines compositions GPU peuvent garder un résidu si on enchaîne
        // des renders sur le même canvas (changement rapide de page/scale).
        ctx.setTransform(1, 0, 0, 1, 0, 0);

        // Le DPR est passé via `transform` au lieu d'un `ctx.setTransform`
        // manuel : pdf.js compose lui-même cette matrice avec le viewport,
        // ce qui évite le miroir vertical observé quand les deux transforms
        // entraient en conflit (B1).
        const transform: [number, number, number, number, number, number] | undefined =
          dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined;

        const task = page.render({ canvasContext: ctx, viewport, transform });
        renderTaskRef.current = task;
        try {
          await task.promise;
        } catch (e) {
          // pdf.js exception name — ne pas remonter sur cancel volontaire.
          if ((e as { name?: string })?.name === 'RenderingCancelledException') return;
          throw e;
        }
        if (renderTaskRef.current === task) renderTaskRef.current = null;
        if (cancelled) return;

        textLayer.replaceChildren();
        const { TextLayer } = await import('pdfjs-dist');
        const textStream = page.streamTextContent({
          includeMarkedContent: true,
          disableNormalization: true,
        });
        const layer = new TextLayer({
          textContentSource: textStream,
          container: textLayer,
          viewport,
        });
        await layer.render();
        if (cancelled) return;

        // Fallback OCR text layer (sprint big-PDF, 2026-05-22).
        // Sur les PDFs purement scannés, `streamTextContent()` ne renvoie
        // rien → pas de spans → sélection souris impossible. On fetch
        // alors les bbox words OCR et on construit un layer custom de
        // spans transparents positionnés, pour rendre la sélection
        // possible. Les pages Mistral OCR (bbox_json.words=[]) n'ont
        // pas de bbox → on ne peut pas construire un text layer
        // précis pour elles (limite connue, cf [[adr_m24_mistral_ocr_2026_05_22]]).
        //
        // Détection "vide" : pdf.js laisse toujours un `<div class="endOfContent">`
        // en marqueur, donc `childElementCount` n'est PAS un indicateur fiable.
        // On compte les vrais <span> sélectionnables.
        const nativeSpans = textLayer.querySelectorAll('span').length;
        if (ocrSourceBaseUrl && nativeSpans === 0) {
          type OcrPagePayload = {
            page_number: number;
            image_size: { w: number; h: number };
            words: { text: string; x: number; y: number; w: number; h: number }[];
          };
          try {
            const ocrPage = await api.get<OcrPagePayload>(
              `${ocrSourceBaseUrl}/ocr/pages/${pageNumber}`,
            );
            if (cancelled) return;
            if (ocrPage.words.length > 0 && ocrPage.image_size.w > 0) {
              const r = viewport.width / ocrPage.image_size.w;
              // Groupement par lignes : 1 span absolute par ligne contient
              // tout le texte de la ligne (joint par espaces). Ça stabilise
              // énormément le caret browser comparé à 1 span par mot
              // (chaque mot était un container isolé, le caret sautait
              // entre eux de manière chaotique). Heuristique de cluster :
              // deux words sont sur la même ligne si l'intervalle vertical
              // de leurs centres y est < min(h1, h2) * 0.6.
              const sorted = [...ocrPage.words].sort((a, b) => {
                if (Math.abs(a.y - b.y) > 5) return a.y - b.y;
                return a.x - b.x;
              });
              type Line = {
                yTop: number;
                yBottom: number;
                xLeft: number;
                words: typeof sorted;
              };
              const lines: Line[] = [];
              for (const w of sorted) {
                const cy = w.y + w.h / 2;
                const last = lines.length > 0 ? lines[lines.length - 1] : null;
                const lastCy = last ? (last.yTop + last.yBottom) / 2 : -1;
                const lastH = last ? last.yBottom - last.yTop : 0;
                if (
                  last &&
                  Math.abs(cy - lastCy) < Math.min(w.h, lastH || w.h) * 0.6
                ) {
                  last.words.push(w);
                  last.yTop = Math.min(last.yTop, w.y);
                  last.yBottom = Math.max(last.yBottom, w.y + w.h);
                  last.xLeft = Math.min(last.xLeft, w.x);
                } else {
                  lines.push({
                    yTop: w.y,
                    yBottom: w.y + w.h,
                    xLeft: w.x,
                    words: [w],
                  });
                }
              }
              const frag = document.createDocumentFragment();
              const spansToRescale: { span: HTMLSpanElement; expectedW: number }[] = [];
              for (const ln of lines) {
                // Trie X dans chaque ligne (déjà fait par le sorted
                // global mais re-trie défensivement après merge).
                ln.words.sort((a, b) => a.x - b.x);
                const span = document.createElement('span');
                span.textContent = ln.words.map((w) => w.text).join(' ');
                span.setAttribute('role', 'presentation');
                const h = ln.yBottom - ln.yTop;
                const lastWord = ln.words[ln.words.length - 1]!;
                const expectedW = (lastWord.x + lastWord.w - ln.xLeft) * r;
                Object.assign(span.style, {
                  position: 'absolute',
                  left: `${ln.xLeft * r}px`,
                  top: `${ln.yTop * r}px`,
                  height: `${h * r}px`,
                  fontSize: `${h * r * 0.95}px`,
                  lineHeight: `${h * r}px`,
                  whiteSpace: 'pre',
                  color: 'transparent',
                  cursor: 'text',
                  userSelect: 'text',
                  pointerEvents: 'auto',
                  transformOrigin: '0 0',
                } satisfies Partial<CSSStyleDeclaration>);
                frag.appendChild(span);
                spansToRescale.push({ span, expectedW });
              }
              textLayer.appendChild(frag);
              // Une fois dans le DOM, on mesure la largeur intrinsèque de
              // chaque ligne (texte rendu avec la fonte sans-serif par
              // défaut du navigateur) et on applique un scaleX horizontal
              // pour aligner précisément les glyphes invisibles sur les
              // glyphes peints du canvas. Sans ça, le caret tombe en biais
              // (la fonte par défaut du browser est ~5-15% plus large ou
              // plus étroite que la fonte du document scanné). Pattern
              // emprunté à pdf.js natif text-layer.
              for (const { span, expectedW } of spansToRescale) {
                const actualW = span.getBoundingClientRect().width;
                if (actualW > 0 && expectedW > 0) {
                  const sx = expectedW / actualW;
                  // Garde-fou : si le ratio est aberrant (mots seuls ou
                  // OCR foireux), on laisse la largeur naturelle pour ne
                  // pas écraser le span en 0px.
                  if (sx > 0.1 && sx < 5) {
                    span.style.transform = `scaleX(${sx})`;
                  }
                }
              }
            }
          } catch {
            // Pas d'OCR pour cette page (404 no_ocr_page) → on laisse
            // le textLayer vide. La sélection reste impossible sur
            // cette page mais ce n'est pas un crash.
          }
        }

        onLayoutReady?.(pageNumber, container);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    }

    void render();
    return () => {
      cancelled = true;
      // Annule le render en cours pour libérer le canvas avant le prochain
      // useEffect (changement de page/scale, unmount).
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
    };
  }, [doc, pageNumber, scale, ocrSourceBaseUrl, onLayoutReady]);

  return (
    <div
      className="relative mx-auto my-4 shadow border border-rule bg-paper-elevated"
      data-page={pageNumber}
    >
      <div ref={containerRef} className="relative">
        <canvas ref={canvasRef} className="block" style={{ position: 'relative', zIndex: 0 }} />

        <div className="highlightLayer" aria-hidden="true">
          {highlights.map((h, i) => (
            <div
              key={`${h.annotationId}-${i}`}
              className={`hl ${HL_CLASS[h.category]}`}
              data-annotation-id={h.annotationId}
              title={h.category}
              style={{
                left: h.x * scale,
                top: h.y * scale,
                width: h.w * scale,
                height: h.h * scale,
              }}
            />
          ))}
          {/* Search-match overlays (Sprint 4-4). Coords are in OCR pixels —
              we project via viewportWidth / ocrImageWidth. Hidden until
              both dimensions are known so we never paint at the wrong scale. */}
          {ocrImageWidth && viewportWidth && searchHighlights.length > 0
            ? searchHighlights.map((s, i) => {
                const r = (viewportWidth / ocrImageWidth) * scale;
                return (
                  <div
                    key={`s-${i}`}
                    className="hl hl-search"
                    style={{
                      left: s.x * r,
                      top: s.y * r,
                      width: s.w * r,
                      height: s.h * r,
                    }}
                  />
                );
              })
            : null}
        </div>

        <div ref={textLayerRef} className="textLayer" />
        {error && (
          <p className="absolute inset-0 flex items-center justify-center text-sm text-red-600">
            {error}
          </p>
        )}
      </div>
      <div className="border-t border-rule px-2 py-1 text-xs text-muted">Page {pageNumber}</div>
    </div>
  );
}

/**
 * React.memo : évite les re-renders quand le parent re-render mais que les
 * props pertinents (doc, pageNumber, scale, highlights, ...) n'ont pas
 * changé. Sprint fluidité viewer 2026-05-23.
 *
 * Notes :
 *  - ``doc`` est stable par ref (créé une fois par pdf.js)
 *  - ``highlights`` et ``searchHighlights`` viennent du parent via Map.get
 *    → tableau ref-stable si le parent memoize sa Map. Sinon, on refait
 *    un shallow check via length + identité ref.
 *  - ``onLayoutReady`` doit être useCallback côté parent pour éviter les
 *    re-renders cascades (sinon le callback change à chaque render parent).
 */
function arePropsEqual(prev: Props, next: Props): boolean {
  if (
    prev.doc !== next.doc ||
    prev.pageNumber !== next.pageNumber ||
    prev.scale !== next.scale ||
    prev.ocrImageWidth !== next.ocrImageWidth ||
    prev.ocrSourceBaseUrl !== next.ocrSourceBaseUrl ||
    prev.onLayoutReady !== next.onLayoutReady
  ) {
    return false;
  }
  // Highlights : ref equal ou same length + same first/last annotationId
  const prevH = prev.highlights ?? [];
  const nextH = next.highlights ?? [];
  if (prevH !== nextH) {
    if (prevH.length !== nextH.length) return false;
    if (prevH.length > 0) {
      if (prevH[0]!.annotationId !== nextH[0]!.annotationId) return false;
      if (prevH[prevH.length - 1]!.annotationId !== nextH[nextH.length - 1]!.annotationId)
        return false;
    }
  }
  const prevS = prev.searchHighlights ?? [];
  const nextS = next.searchHighlights ?? [];
  if (prevS !== nextS) {
    if (prevS.length !== nextS.length) return false;
  }
  return true;
}

const PdfPage = memo(PdfPageInner, arePropsEqual);

/**
 * Wrapper lazy-mount via IntersectionObserver — sprint fluidité viewer
 * 2026-05-23. Évite de monter <PdfPage> (qui lance pdf.js render + OCR
 * fetch + textLayer build) pour des pages hors viewport, ce qui rend
 * le scroll très rapide sur de gros dossiers (100+ pages).
 *
 * Stratégie : placeholder div de taille estimée (ratio A4) jusqu'à ce
 * que la page rentre dans la rootMargin (1 viewport au-dessus + 2
 * viewports en-dessous, pour préfetcher du scroll vers le bas). Une
 * fois mounté, on garde le composant monté pour éviter le yo-yo si
 * l'utilisateur scrolle de haut en bas.
 *
 * Dimensions placeholder : A4 ratio (1/√2 ≈ 0.707) appliqué à une
 * largeur de référence basée sur ``scale × 595`` (mediabox A4 en
 * points). Pas pixel-perfect mais évite le saut visuel disgracieux.
 */
function LazyPdfPage(props: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  useEffect(() => {
    if (hasBeenVisible) return;
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setHasBeenVisible(true);
            observer.disconnect();
            break;
          }
        }
      },
      // Prefetch : 1 viewport au-dessus, 2 en dessous (sens lecture).
      { rootMargin: '100% 0% 200% 0%', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasBeenVisible]);

  if (hasBeenVisible) {
    return <PdfPage {...props} />;
  }
  // Placeholder dimensions : ratio A4 (1/√2). En pratique le PDF source
  // peut être différent (orientation paysage, format US Letter, etc.)
  // mais c'est assez proche pour préserver la scroll position.
  const estWidth = 595 * props.scale;
  const estHeight = estWidth / 0.707;
  return (
    <div
      ref={wrapperRef}
      data-page={props.pageNumber}
      className="relative mx-auto my-4 border border-rule bg-paper-card text-text-tertiary"
      style={{ width: `${estWidth}px`, height: `${estHeight}px` }}
    >
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[11px]">
        Page {props.pageNumber} — chargement à l'approche du viewport…
      </span>
    </div>
  );
}

export default LazyPdfPage;
